import React, { useEffect, useState } from 'react';

import { ControlLabel, Form, FormControl } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';

import ActivityFormField from '../ActivityForm/ActivityFormField';
import CollapsableItem from '../../utils/CollapsableItem';
import { isMobileResolution } from '../../../infrastructure/utils/common';
import ActivitySectionButtons from '../ActivitySectionButtons';
import SimpleSelect from '../../utils/FormWidgets/SimpleSelect';
import ActivityFormFieldWrapper from '../ActivityForm/ActivityFormFieldWrapper';
import { CANCELLATION_POLICY_OPTIONS } from '../../../infrastructure/constants/activities';
import {
  getIsNotIntegrationEnabled,
  hasNoBookingPlatformIntegration,
} from '../../forms/EditPeekAndRedeamOrderForm/helpers';
import { Activity } from '../../../types/Activity';
import { PartyType } from '../../../types/Party';
import ActivityFormMultiSelector from '../ActivityForm/ActivityFormMultiSelector/ActivityFormMultiSelector';
import ActivitiesAutoComplete, {
  ActivityItem,
} from '../../utils/AutoComplete/ActivitiesAutoComplete';

import ActivityMobileMetaFields from './ActivityMobileMetafields';
import ActivityAvailableDays from './ActivityAvailableDays/ActivityAvailableDays';
import CitySelector from './CitySelector';

const getMetafieldsFromActivity = (
  activity: Activity,
  isBookingPlatformIntegrationAbsent: boolean
): Metafields => ({
  city: activity.city,
  popularity: activity.popularity,
  hours: activity.hours,
  cancellationPolicy: activity.cancellationPolicy,
  bachTip: activity.bachTip,
  deadline: isBookingPlatformIntegrationAbsent ? activity.deadline : 0,
  partyTypes: activity.partyTypes || [],
  suggestedActivityId: activity.suggestedActivityId,
  suggestedActivityText: activity.suggestedActivityText,
});

export type Metafields = Pick<
  Activity,
  | 'city'
  | 'popularity'
  | 'hours'
  | 'cancellationPolicy'
  | 'bachTip'
  | 'deadline'
  | 'suggestedActivityId'
  | 'suggestedActivityText'
> &
  Required<Pick<Activity, 'partyTypes'>>;

type Props = {
  activity: Activity;
  updateActivity(activity: Partial<Activity>): Promise<Activity | undefined>;
  partyTypes: PartyType[];
  suggestedActivity?: ActivityItem;
};
const ActivityMetaFields = ({
  activity,
  updateActivity,
  partyTypes,
  suggestedActivity,
}: Props) => {
  const { source } = activity;
  const isNotIntegrationEnabled = getIsNotIntegrationEnabled(source);
  const isBookingPlatformIntegrationAbsent = hasNoBookingPlatformIntegration(
    source
  );
  const [initialMetafields, setInitialMetafields] = useState<Metafields>(
    getMetafieldsFromActivity(activity, isBookingPlatformIntegrationAbsent)
  );
  const [metafields, setMetaFields] = useState<Metafields>(
    getMetafieldsFromActivity(activity, isBookingPlatformIntegrationAbsent)
  );

  const [availablePartyTypes, setAvailablePartyTypes] = useState<string[]>([]);

  useEffect(() => {
    const set = new Set<string>(partyTypes.map(t => t.id));
    (activity.partyTypes || [])?.forEach(t => set.delete(t));
    setAvailablePartyTypes(Array.from(set));
  }, [partyTypes]);

  const onMetaFieldsChange = values => {
    const changedValues = {};
    values.forEach(({ name, value }) => {
      let val = value;
      if (name === 'popularity') {
        val = +value;
      }
      if (name === 'deadline') {
        val = +value * 24;
      }
      if (name === 'partyTypes') {
        const currentTypes = metafields.partyTypes || [];
        if (currentTypes.includes(value)) {
          val = currentTypes.filter(t => t !== value);
          setAvailablePartyTypes([...availablePartyTypes, value]);
        } else {
          val = Array.from(new Set([...currentTypes, value]));
          setAvailablePartyTypes(availablePartyTypes.filter(t => t !== value));
        }
      }
      if (name === 'suggestedActivityId') {
        if (value === undefined) {
          val = '';
        }
      }
      changedValues[name] = val;
    });

    setMetaFields({
      ...metafields,
      ...changedValues,
    });
  };

  const handleChangeCity = (value: string) => {
    setMetaFields({ ...metafields, city: value });
  };

  const onSaveClick = async event => {
    event.preventDefault();
    const result = await updateActivity(metafields);
    if (result) {
      setInitialMetafields(
        getMetafieldsFromActivity(result, isBookingPlatformIntegrationAbsent)
      );
    }
  };

  const onResetClick = () => {
    setMetaFields(initialMetafields);
  };

  const werePropertiesChanged = () => !isEqual(metafields, initialMetafields);

  useEffect(() => {
    setMetaFields(prevMetafields => {
      if (!isBookingPlatformIntegrationAbsent) {
        return { ...prevMetafields, deadline: 0 };
      } else {
        return { ...prevMetafields };
      }
    });
  }, [isBookingPlatformIntegrationAbsent]);

  if (isMobileResolution()) {
    return (
      <>
        <ActivityMobileMetaFields
          metafields={metafields}
          onMetaFieldsChange={onMetaFieldsChange}
          onSaveClick={onSaveClick}
          onResetClick={onResetClick}
          werePropertiesChanged={werePropertiesChanged()}
          hasNativeMetafields={activity.hasNativeMetafields}
          isNotIntegrationEnabled={isNotIntegrationEnabled}
        />
      </>
    );
  }

  return (
    <div className='data-box'>
      <div className='activity-section-top'>
        <h4 className='header'>Experience Metafields</h4>
        <ActivitySectionButtons
          onSaveClick={onSaveClick}
          onResetClick={onResetClick}
          disabled={!werePropertiesChanged()}
        />
      </div>
      <CollapsableItem isExpanded>
        <Form>
          <CitySelector
            city={metafields.city}
            handleChangeCity={handleChangeCity}
          />
          {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityFormField
              label='Popularity'
              name='popularity'
              value={metafields.popularity}
              onMetaFieldsChange={onMetaFieldsChange}
            />
          }
          {!isNotIntegrationEnabled ? (
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityAvailableDays
              label='Availability'
              value={metafields.hours}
              initialValue={activity.hours}
              onMetaFieldsChange={onMetaFieldsChange}
            />
          ) : null}
          {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityFormField
              labelStyle={{ width: '35%' }}
              label='Booking Cutoff (Input a number of days. If no value is input, default is 7)'
              name='deadline'
              disabled={!isBookingPlatformIntegrationAbsent}
              value={
                typeof metafields.deadline === 'number'
                  ? metafields.deadline / 24
                  : ''
              }
              onMetaFieldsChange={onMetaFieldsChange}
            />
          }
          {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityFormFieldWrapper>
              <ControlLabel>Cancellation Policy</ControlLabel>
              <SimpleSelect
                // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
                // @ts-ignore
                style={{ flex: 1, height: '58px' }}
                onChange={value =>
                  setMetaFields({ ...metafields, cancellationPolicy: value })
                }
                value={metafields.cancellationPolicy}
                name='cancellationPolicy'
                data={[
                  {
                    label: 'Generous',
                    value: CANCELLATION_POLICY_OPTIONS.get('Generous'),
                  },
                  {
                    label: 'Moderate',
                    value: CANCELLATION_POLICY_OPTIONS.get('Moderate'),
                  },
                  {
                    label: 'Strict',
                    value: CANCELLATION_POLICY_OPTIONS.get('Strict'),
                  },
                ]}
              />
            </ActivityFormFieldWrapper>
          }
          {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityFormField
              label='Bach-Tip'
              name='bachTip'
              value={metafields.bachTip}
              onMetaFieldsChange={onMetaFieldsChange}
            />
          }
          <hr />
          {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityFormMultiSelector
              labelStyle={{ width: '12%' }}
              label='Party Types'
              caption={'Applied Party Types'}
              selectedItems={metafields.partyTypes}
              options={availablePartyTypes}
              onItemClick={type =>
                onMetaFieldsChange([{ name: 'partyTypes', value: type }])
              }
            />
          }

          <hr />

          <ControlLabel>Suggested Activity</ControlLabel>

          <ActivitiesAutoComplete
            key={`suggestedActivity:${suggestedActivity?.id}`}
            filterData={{ location: activity.city }}
            initialValue={suggestedActivity}
            onSelect={id =>
              onMetaFieldsChange([{ name: 'suggestedActivityId', value: id }])
            }
            clearable
          />

          <ControlLabel>Suggested Activity Text</ControlLabel>

          <FormControl
            componentClass='textarea'
            value={metafields.suggestedActivityText}
            onChange={e =>
              onMetaFieldsChange([
                { name: 'suggestedActivityText', value: e.target.value },
              ])
            }
          />
        </Form>
      </CollapsableItem>
    </div>
  );
};

export default React.memo(ActivityMetaFields);
